import React from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { Tonality } from '@dsny/dsny-component-library'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { browserHelpers } from 'utils'
import { PlayArrowFilled, Info } from '@dsny/dsny-component-library/icons'
import Tooltip from 'components/Tooltip'
import PlaceholderLogo from 'images/icons/placeholder.png'
import ReleaseDownloadDropdown from 'components/ReleaseDownloadDropdown/ReleaseDownloadDropdown'
import {
  ReleaseSearchCardContainer,
  ImageContainer,
  ReleaseSearchCardImage,
  ActionBarAbsoluteContainer,
  ActionBar,
  ActionButton,
  TextContainer
} from './ReleaseSearchCard.styles'

export default function ReleaseSearchCard({
  release,
  onShowModal,
  handlePlayRelease,
  handleDownloadRelease
}) {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMobileMediaQuery()

  const onPlayRelease = (e) => {
    e.stopPropagation()
    handlePlayRelease(release)
  }

  const onDownloadRelease = (format) => {
    handleDownloadRelease(release, format)
  }

  const onInfoReleaseClick = (e) => {
    e.stopPropagation()
    navigate(`/releases/releaseInfo/${release.releaseid}`)
  }

  const onReleaseClick = () => {
    if (isMobile) {
      navigate(`/releases/releaseInfo/${release.releaseid}`)
    } else {
      onShowModal(release)
    }
  }

  return (
    <ReleaseSearchCardContainer
      data-cy="search-release-card"
      data-release-id={release?.releaseid}
      onClick={onReleaseClick}
    >
      <ImageContainer>
        <ReleaseSearchCardImage
          src={release?.thumbnail || release?.coverart || PlaceholderLogo}
          alt="album cover"
        />

        {!isMobile && (
          <ActionBarAbsoluteContainer className="action_bar">
            <ActionBar>
              <Tooltip hasArrow={false} placement="top" text="Play release">
                <ActionButton
                  data-cy="search-release-card-info"
                  variant="primary"
                  setting="player"
                  mode="dark"
                  onClick={onPlayRelease}
                >
                  <PlayArrowFilled
                    width={16}
                    height={16}
                    color={theme.colors.black0}
                  />
                </ActionButton>
              </Tooltip>

              {browserHelpers.browserAllowsDownload && (
                <ReleaseDownloadDropdown
                  release={release}
                  onDownloadRelease={onDownloadRelease}
                  ButtonComponent={ActionButton}
                  iconSize={16}
                />
              )}

              <Tooltip hasArrow={false} placement="top" text="Release info">
                <ActionButton
                  variant="primary"
                  setting="player"
                  mode="dark"
                  onClick={onInfoReleaseClick}
                >
                  <Info width={16} height={16} color={theme.colors.black0} />
                </ActionButton>
              </Tooltip>
            </ActionBar>
          </ActionBarAbsoluteContainer>
        )}
      </ImageContainer>

      <TextContainer>
        <Tonality.UIText
          textStyle="bodyBold"
          color={theme.colors.neutralW100}
          className="truncate"
        >
          {release?.title}
        </Tonality.UIText>
        <Tonality.UIText
          textStyle="body"
          color={theme.colors.neutralW70}
          className="truncate"
        >
          {release?.artist}
        </Tonality.UIText>
      </TextContainer>
    </ReleaseSearchCardContainer>
  )
}

ReleaseSearchCard.propTypes = {
  release: PropTypes.shape({
    releaseid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    artist: PropTypes.string,
    thumbnail: PropTypes.string,
    coverart: PropTypes.string,
    candownload: PropTypes.bool
  }).isRequired,
  onShowModal: PropTypes.func.isRequired,
  handlePlayRelease: PropTypes.func.isRequired,
  handleDownloadRelease: PropTypes.func.isRequired
}
